import '../../00group-overlay/dees-speechbubble/dees-speechbubble.js';
import * as plugins from '../../00plugins.js';
import * as interfaces from '../../interfaces/index.js';
import { zIndexLayers } from '../../00zindex.js';

import {
  DeesElement,
  type TemplateResult,
  property,
  customElement,
  html,
  css,
  cssManager,
} from '@design.estate/dees-element';
import { demoFunc } from './dees-appui-mainmenu.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

/**
 * the most left menu
 * usually used as organization selector
 */
@customElement('dees-appui-mainmenu')
export class DeesAppuiMainmenu extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['App UI'];

  // INSTANCE

  // Logo properties
  @property({ type: String })
  accessor logoIcon: string = '';

  @property({ type: String })
  accessor logoText: string = '';

  // Menu groups (new way)
  @property({ type: Array })
  accessor menuGroups: interfaces.IMenuGroup[] = [];

  // Bottom tabs (pinned to bottom)
  @property({ type: Array })
  accessor bottomTabs: interfaces.IMenuItem[] = [];

  // Legacy tabs property (for backward compatibility)
  @property({ type: Array })
  accessor tabs: interfaces.IMenuItem[] = [];

  @property()
  accessor selectedTab!: interfaces.IMenuItem;

  @property({ type: Boolean, reflect: true })
  accessor collapsed: boolean = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      @media (prefers-reduced-motion: reduce) { *, *::before, *::after { transition: none !important; animation: none !important; } }

      :host {
        --menu-width-expanded: 200px;
        --menu-width-collapsed: 56px;
        --tooltip-bg: var(--dees-color-tooltip-bg);
        --tooltip-fg: var(--dees-color-tooltip-fg);
        position: relative;
        display: block;
        height: 100%;
      }

      .mainContainer {
        color: var(--dees-color-text-secondary);
        z-index: ${zIndexLayers.fixed.appBar};
        display: flex;
        flex-direction: column;
        position: relative;
        width: var(--menu-width-expanded);
        height: 100%;
        background: var(--dees-appui-chrome-bg);
        user-select: none;
        border-right: 1px solid var(--dees-color-border-subtle);
        font-family: var(--dees-font-family);
        transition: width var(--dees-transition-slow) var(--dees-ease-standard);
      }

      :host([collapsed]) .mainContainer {
        width: var(--menu-width-collapsed);
      }

      .collapse-toggle { display: grid; place-items: center; flex-shrink: 0; margin-left: auto; width: 28px; height: 28px; padding: 0; border: 0; border-radius: var(--dees-radius-md); background: transparent; color: var(--dees-color-text-secondary); cursor: pointer; }
      :host([collapsed]) .collapse-toggle { margin-inline: auto; width: 48px; }
      .collapse-toggle:hover { background: var(--dees-color-hover); }
      button:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: -2px; }
      :host([collapsed]) .logoSection { padding: 0 4px; }
      :host([collapsed]) .logoSection .logoIcon { display: none; }
      /* Logo Section */
      .logoSection {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        height: var(--dees-appui-row-height);
        padding: 0 14px;
        flex-shrink: 0;
        box-sizing: border-box;
      }

      .logoSection .logoIcon {
        font-size: var(--dees-font-title2-size);
        color: var(--dees-color-text-primary);
        flex-shrink: 0;
      }

      .logoSection .logoText {
        flex: 1;
        font-size: var(--dees-font-subhead-size);
        font-weight: 600;
        color: var(--dees-color-text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        transition: opacity var(--dees-transition-default) var(--dees-ease-standard), width var(--dees-transition-slow) var(--dees-ease-standard);
      }

      :host([collapsed]) .logoSection {
        justify-content: center;
        padding: 0;
        gap: 0;
      }

      :host([collapsed]) .logoSection .logoText {
        display: none;
      }

      /* Middle Section (scrollable) */
      .menuSection {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        overscroll-behavior: contain;
        padding: var(--dees-spacing-sm) 0;
      }

      .menuSection::-webkit-scrollbar {
        width: 6px;
      }

      .menuSection::-webkit-scrollbar-track {
        background: transparent;
      }

      .menuSection::-webkit-scrollbar-thumb {
        background: var(--dees-color-scrollbar-thumb);
        border-radius: var(--dees-radius-xs);
      }

      .menuSection::-webkit-scrollbar-thumb:hover {
        background: var(--dees-color-scrollbar-thumb-hover);
      }

      /* Menu Group */
      .menuGroup {
        padding: 0 var(--dees-spacing-sm);
        margin-bottom: var(--dees-spacing-sm);
      }

      .menuGroup:last-child {
        margin-bottom: 0;
      }

      .groupHeader {
        padding: 8px 12px 6px;
        font-size: var(--dees-font-caption2-size);
        font-weight: 600;
        color: var(--dees-color-text-muted);
        text-transform: none;
        letter-spacing: normal;
        white-space: nowrap;
        overflow: hidden;
        transition: opacity var(--dees-transition-default) var(--dees-ease-standard), max-height var(--dees-transition-slow) var(--dees-ease-standard);
        max-height: 30px;
      }

      :host([collapsed]) .groupHeader {
        opacity: 0;
        max-height: 0;
        padding: 0;
        margin: 0;
      }

      .groupTabs {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      :host([collapsed]) .menuGroup {
        padding: 0 4px;
      }

      /* Tab Item */
      .tab {
        border: 0; background: transparent; width: 100%; text-align: left; font-family: inherit; min-height: 32px;
        position: relative;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-md);
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        font-size: var(--dees-font-control-size);
        font-weight: var(--dees-font-control-weight);
        border-radius: var(--dees-radius-md);
        cursor: pointer;
        transition: background var(--dees-transition-fast) var(--dees-ease-standard), color var(--dees-transition-fast) var(--dees-ease-standard);
        color: var(--dees-color-text-secondary);
      }

      .tab:hover {
        background: var(--dees-color-hover);
        color: var(--dees-color-text-primary);
      }

      .tab:active {
        background: var(--dees-color-active);
      }

      /* Sidebar selection: a systemBlue-tinted filled rounded rect with
         systemBlue label/icon (no left-edge bar). */
      .tab.selectedTab {
        background: var(--dees-color-nav-selected);
        color: var(--dees-color-accent-primary);
      }

      .tab dees-icon {
        font-size: 16px;
        opacity: 0.85;
        flex-shrink: 0;
      }

      .tab.selectedTab dees-icon {
        opacity: 1;
      }

      .tab .tabLabel {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        transition: opacity 0.2s ease, width 0.25s ease;
      }

      /* Collapsed tab styles */
      :host([collapsed]) .tab {
        justify-content: center;
        padding: var(--dees-spacing-sm);
        gap: 0;
      }

      :host([collapsed]) .tab .tabLabel {
        opacity: 0;
        width: 0;
        position: absolute;
      }

      /* Badge styles */
      .badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        padding: 0 6px;
        font-size: var(--dees-font-caption2-size);
        font-weight: 600;
        border-radius: var(--dees-radius-full);
        margin-left: auto;
      }

      .badge.default {
        background: var(--dees-color-badge-default-bg);
        color: var(--dees-color-badge-default-fg);
      }

      .badge.success {
        background: var(--dees-color-badge-success-bg);
        color: var(--dees-color-badge-success-fg);
      }

      .badge.warning {
        background: var(--dees-color-badge-warning-bg);
        color: var(--dees-color-badge-warning-fg);
      }

      .badge.error {
        background: var(--dees-color-badge-error-bg);
        color: var(--dees-color-badge-error-fg);
      }

      :host([collapsed]) .badge {
        display: grid; place-items: center; position: absolute; top: 0; right: 0;
        min-width: 14px; height: 14px; padding: 0 3px; margin: 0; font-size: 9px;
      }

      /* Bottom Section */
      .bottomSection {
        flex-shrink: 0;
        padding: var(--dees-spacing-sm);
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      :host([collapsed]) .bottomSection {
        padding: 8px 4px;
      }
      @media (pointer: coarse) { .tab { min-height: 44px; } .collapse-toggle { min-width: 44px; min-height: 44px; } :host([collapsed]) .logoSection { padding: 0 4px; } }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="mainContainer">
        <div class="logoSection">
            ${this.logoIcon ? html`<dees-icon class="logoIcon" .icon="${this.logoIcon}"></dees-icon>` : ''}
            ${this.logoText ? html`<span class="logoText">${this.logoText}</span>` : ''}
            <button type="button" class="collapse-toggle" aria-label=${this.collapsed ? 'Expand sidebar' : 'Collapse sidebar'} aria-expanded=${!this.collapsed} @click=${() => this.toggleCollapse()}>
              <dees-icon .icon=${this.collapsed ? 'lucide:PanelLeftOpen' : 'lucide:PanelLeftClose'} .iconSize=${16}></dees-icon>
            </button>
            <dees-speechbubble hint .disabled=${!this.collapsed} text="Expand sidebar"></dees-speechbubble>
          </div>

        <nav class="menuSection" aria-label="Main navigation">
          ${this.menuGroups.length > 0 ? this.renderMenuGroups() : this.renderLegacyTabs()}
        </nav>

        ${this.bottomTabs.length > 0 ? html`
          <div class="bottomSection">
            ${this.bottomTabs.map((tabArg) => this.renderTab(tabArg))}
          </div>
        ` : ''}
      </div>

    `;
  }

  private renderMenuGroups(): TemplateResult {
    return html`
      ${this.menuGroups.map((group) => html`
        <div class="menuGroup">
          ${group.name ? html`<div class="groupHeader">${group.name}</div>` : ''}
          <div class="groupTabs">
            ${group.items.map((tabArg) => this.renderTab(tabArg))}
          </div>
        </div>
      `)}
    `;
  }

  private renderLegacyTabs(): TemplateResult {
    return html`
      <div class="menuGroup">
        <div class="groupTabs">
          ${this.tabs.map((tabArg) => this.renderTab(tabArg))}
        </div>
      </div>
    `;
  }

  private renderTab(tabArg: interfaces.IMenuItem): TemplateResult {
    const label = tabArg.label ?? tabArg.key;
    const selected = tabArg.key === this.selectedTab?.key;
    return html`<button type="button" class="tab ${selected ? 'selectedTab' : ''}"
      aria-label=${label} aria-current=${selected ? 'page' : 'false'}
      @keydown=${this.handleNavigationKey} @click=${() => this.updateTab(tabArg)}>
      ${tabArg.iconName ? html`<dees-icon .icon=${tabArg.iconName}></dees-icon>` : ''}
      <span class="tabLabel">${label}</span>
      ${tabArg.badge !== undefined ? html`<span class="badge ${tabArg.badgeVariant || 'default'}">${tabArg.badge}</span>` : ''}
    </button><dees-speechbubble hint .disabled=${!this.collapsed} .text=${label}></dees-speechbubble>`;
  }

  private handleNavigationKey(event: KeyboardEvent) {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const buttons = [...this.shadowRoot!.querySelectorAll<HTMLButtonElement>('.tab')];
    const index = buttons.indexOf(event.currentTarget as HTMLButtonElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next]?.focus();
  }

  private getAllTabs(): interfaces.IMenuItem[] {
    if (this.menuGroups.length > 0) {
      const groupTabs = this.menuGroups.flatMap(group => group.items);
      return [...groupTabs, ...this.bottomTabs];
    }
    return [...this.tabs, ...this.bottomTabs];
  }

  updateTab(tabArg: interfaces.IMenuItem) {
    this.selectedTab = tabArg;
    this.selectedTab.action();

    // Emit tab-select event
    this.dispatchEvent(new CustomEvent('tab-select', {
      detail: { tab: tabArg },
      bubbles: true,
      composed: true
    }));
  }

  firstUpdated() {
    const allTabs = this.getAllTabs();
    if (allTabs.length > 0 && !this.selectedTab) {
      this.updateTab(allTabs[0]);
    }
  }

  public toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    this.dispatchEvent(new CustomEvent('collapse-change', {
      detail: { collapsed: this.collapsed },
      bubbles: true,
      composed: true
    }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dees-appui-mainmenu': DeesAppuiMainmenu;
  }
}
